/**
 * useMatterChangeDetection composable
 *
 * Handles matter change detection and confirmation flow for fixed fees.
 * Used by CreateChargePanel and CreateChargeModal to avoid code duplication.
 *
 * The flow:
 * 1. Check if matter has changed (originalMatterId vs newMatterId)
 * 2. Call bulkChangeMatter API without confirmation
 * 3. If unbilled entries exist, show confirmation dialog
 * 4. On confirm, call bulkChangeMatter with confirmation flag
 * 5. On cancel, revert to original matter
 */

import { reactive } from 'vue';
import ChargeService from '@/services/ChargeService';
export function useMatterChangeDetection() {
  // State for pending matter change (used to populate confirmation dialog message)
  const pendingMatterChange = reactive({
    chargeId: null,
    targetMatterId: null,
    totalUnbilledEntryCount: 0,
    chargesWithUnbilledEntries: 0
  });

  /**
   * Check if matter has changed
   */
  const hasMatterChanged = (originalMatterId, newMatterId) => {
    return originalMatterId && newMatterId && originalMatterId !== newMatterId;
  };

  /**
   * Calculate new fixed fee amount if changed
   */
  const calculateAmountChange = (currentAmount, originalAmount) => {
    const current = Number(currentAmount) || 0;
    const original = Number(originalAmount) || 0;
    return current !== original ? current : null;
  };

  /**
   * Handle matter change with confirmation flow
   */
  const handleMatterChange = async ({
    chargeId,
    originalMatterId,
    newMatterId,
    showConfirmation
  }) => {
    if (!hasMatterChanged(originalMatterId, newMatterId)) {
      return {
        success: true,
        newOriginalMatterId: null
      };
    }
    const result = await ChargeService.bulkChangeMatter([chargeId], newMatterId, false);
    if (result.requiresConfirmation) {
      pendingMatterChange.chargeId = chargeId;
      pendingMatterChange.targetMatterId = newMatterId;
      pendingMatterChange.totalUnbilledEntryCount = result.totalUnbilledEntryCount || 0;
      pendingMatterChange.chargesWithUnbilledEntries = result.chargesWithUnbilledEntries || 0;
      const confirmed = await showConfirmation();

      // Clear pending data
      Object.assign(pendingMatterChange, {
        chargeId: null,
        targetMatterId: null,
        totalUnbilledEntryCount: 0,
        chargesWithUnbilledEntries: 0
      });
      if (!confirmed) {
        await ChargeService.bulkChangeMatter([chargeId], originalMatterId, false);
        return {
          success: true,
          newOriginalMatterId: null
        };
      }
      const confirmedResult = await ChargeService.bulkChangeMatter([chargeId], newMatterId, true);
      if (!confirmedResult.success && confirmedResult.successCount === 0) {
        throw new Error(confirmedResult.errors?.map(e => e.reason || e.message || e).join(', ') || 'Failed to change matter');
      }
      return {
        success: true,
        newOriginalMatterId: newMatterId
      };
    }
    if (!result.success && result.successCount === 0) {
      throw new Error(result.errors?.map(e => e.reason || e.message || e).join(', ') || 'Failed to change matter');
    }
    return {
      success: true,
      newOriginalMatterId: newMatterId
    };
  };

  /**
   * Build confirmation message based on pending matter change data
   */
  const getMatterChangeMessage = () => {
    if (pendingMatterChange.totalUnbilledEntryCount > 0) {
      const count = pendingMatterChange.totalUnbilledEntryCount;
      return `This fixed fee has ${count} unbilled time ${count === 1 ? 'entry' : 'entries'} that will also be moved to the new matter. The rates for these entries will be recalculated based on the new matter's rate settings. Do you want to continue?`;
    }
    return 'Are you sure you want to change the matter for this fixed fee?';
  };
  return {
    pendingMatterChange,
    hasMatterChanged,
    handleMatterChange,
    getMatterChangeMessage,
    calculateAmountChange
  };
}